import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Plus, Edit, Trash2, Search, Download, Filter, X, Trash, Calendar, Upload, FileSpreadsheet, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Copy, User, Eye, AlertTriangle, CheckCircle2, FileDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DatePicker } from '../../components/DatePicker';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import UserAvatar from '../../components/UserAvatar';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { ActiveFilterChips } from '../../components/ActiveFilterChips';
import {
  PlatformIcon,
  findPlatformLink,
  useSocialMediaOptions,
} from '../../utils/socialMediaOptions';
import { resolveBadgeStyle } from '../../utils/badgeColors';
import { csvSafe } from '../../utils/csvSafe';
import PostDetailModal from '../../components/SocialMedia/PostDetailModal';
import MultiLinkInput from '../../components/MultiLinkInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sort indicator component - moved outside to avoid nested component warning
const SortIndicator = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return <ChevronsUpDown size={14} className="text-slate-400" />;
  }
  return sortDirection === 'asc' 
    ? <ChevronUp size={14} className="text-cyan-600" />
    : <ChevronDown size={14} className="text-cyan-600" />;
};

// Get current month's start and end dates
const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  // First day of current month (1st)
  const firstDay = new Date(year, month, 1);
  
  // Last day of current month
  // Setting day to 0 of next month gives us the last day of current month
  const lastDay = new Date(year, month + 1, 0);
  
  // Format dates as YYYY-MM-DD without timezone conversion
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  return {
    from: formatDate(firstDay),
    to: formatDate(lastDay)
  };
};

const PostsTable = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { PLATFORMS, PLATFORM_ICONS, ACCOUNTS, COLLEGES_FOR_SELECT: COLLEGES, CONTENT_TYPES, LANGUAGES, STATUSES, ACCOUNT_COLORS, COLLEGE_COLORS, CONTENT_TYPE_COLORS } = useSocialMediaOptions();
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    platform: [],
    account: [],
    college: [],
    content_type: [],
    language: [],
    status: [],
    created_by: [],   // multi-select user IDs
    assigned_to: []   // multi-select user IDs
  });
  
  // No date filter by default so new users see all their posts immediately
  // instead of an empty table scoped to the current calendar month.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateField, setDateField] = useState('created_at'); // Default to Created Date
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // Post Detail Modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPostDetail, setSelectedPostDetail] = useState(null);

  // Bulk-upload result (row-wise errors / warnings shown in a modal)
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploadResultOpen, setIsUploadResultOpen] = useState(false);
  
  // Sorting state - default to most recently created first
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Use centralized permissions from PermissionsContext (RBAC-backed).
  const { isReadOnly, canModify } = useAuth();
  const { canDeleteSocialMedia, canModifySocialMedia } = usePermissions();
  // Creative Head can modify but cannot delete
  const canDelete = canDeleteSocialMedia();
  
  const [bulkUpdateData, setBulkUpdateData] = useState({
    platforms: [],
    account: '',
    college: '',
    content_type: '',
    language: '',
    status: '',
    assigned_to: ''
  });
  const [formData, setFormData] = useState({
    content_name: '',
    platforms: [],
    account: '',
    college: '',
    content_type: '',
    language: '',
    host_by: '',
    approved_for_posting: false,
    content_received_date: '',
    scheduled_date: '',
    posted_date: '',
    status: 'Yet to Shoot',
    notes: '',
    links: [],
    assigned_to: ''
  });

  useEffect(() => {
    loadPosts();
    loadUsers();
  }, []);

  // Deep link from a notification (?highlight=<post_id>) — open the exact
  // Post Details modal. Fast path: reuse the already-loaded `posts` list
  // (no extra call). Fallback: fetch the single post directly — this
  // re-enforces the same post-visibility RBAC rule as everywhere else, so a
  // stale/forged link can never show more than the Posts page itself would.
  // Keyed off `location.search` (not a one-shot ref) so a second
  // notification click while already on /social-media/posts — same route,
  // same mounted component — still re-triggers this effect.
  // `handledHighlightIdRef` just prevents re-opening the SAME id twice.
  const handledHighlightIdRef = React.useRef(null);
  useEffect(() => {
    if (loading) return;
    const highlightId = new URLSearchParams(location.search).get('highlight');
    if (!highlightId || handledHighlightIdRef.current === highlightId) return;
    handledHighlightIdRef.current = highlightId;

    (async () => {
      const existing = posts.find(p => p.id === highlightId);
      if (existing) {
        setSelectedPostDetail(existing);
        setIsDetailModalOpen(true);
      } else {
        try {
          const res = await axios.get(`${API}/social-media/posts/${highlightId}`, { headers: { Authorization: `Bearer ${token}` } });
          setSelectedPostDetail(res.data);
          setIsDetailModalOpen(true);
        } catch (error) {
          toast.error(
            error.response?.status === 404
              ? "This post no longer exists or you don't have access to it."
              : 'Failed to open the post from this notification.'
          );
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('highlight');
      navigate(url.pathname + url.search, { replace: true });
      // Reset so clicking the SAME notification again later still re-opens
      // the modal instead of being silently ignored.
      handledHighlightIdRef.current = null;
    })();
  }, [posts, loading, location.search]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown';
  };

  const loadPosts = async () => {
    try {
      // All categorical filtering (platform/account/college/content_type/
      // language/status/created_by/assigned_to) happens client-side in
      // `filteredPosts` below — this keeps multi-select filtering instant
      // with zero extra network calls per checkbox click. The backend still
      // applies RBAC scoping regardless of query params.
      const response = await axios.get(`${API}/social-media/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
      // Refresh the "Posts" sidebar attention badge — this fires after every
      // create/edit/delete/duplicate/bulk action since they all call loadPosts().
      window.dispatchEvent(new Event('social-media-posts-changed'));
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  // Required-field validation for Create/Edit Post modal. Returns a
  // { field: message } map — empty object means valid. Same pattern as the
  // Task modal so the inline-error UX feels consistent.
  const validatePostForm = (data) => {
    const errors = {};
    if (!data.content_name || !data.content_name.trim()) {
      errors.content_name = 'Content name is required';
    }
    if (!data.platforms || data.platforms.length === 0) {
      errors.platforms = 'Select at least one platform';
    }
    if (!data.account) errors.account = 'Account is required';
    if (!data.college) errors.college = 'College is required';
    if (!data.content_type) errors.content_type = 'Content type is required';
    if (!data.language) errors.language = 'Language is required';
    if (!data.host_by || !data.host_by.trim()) errors.host_by = 'Host By is required';
    if (!data.status) errors.status = 'Status is required';
    if (!data.assigned_to) errors.assigned_to = 'Assigned To is required';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validatePostForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }
    setFormErrors({});

    // Soft warning only — never blocks saving. Excludes the post itself when editing.
    const matches = findClashingPosts(formData.account, formData.college, formData.scheduled_date, editingPost?.id);
    if (matches.length > 0) {
      setClashWarning({ matches });
      return;
    }

    await savePost();
  };

  const savePost = async () => {
    try {
      const submitData = { ...formData };
      
      // Convert dates to ISO strings if they exist, otherwise set to null
      submitData.content_received_date = submitData.content_received_date 
        ? new Date(submitData.content_received_date).toISOString() 
        : null;
      submitData.scheduled_date = submitData.scheduled_date 
        ? new Date(submitData.scheduled_date).toISOString() 
        : null;
      submitData.posted_date = submitData.posted_date 
        ? new Date(submitData.posted_date).toISOString() 
        : null;
      
      // Ensure empty strings are null
      if (!submitData.account) submitData.account = null;
      if (!submitData.college) submitData.college = null;
      if (!submitData.content_type) submitData.content_type = null;
      if (!submitData.language) submitData.language = null;
      if (!submitData.host_by) submitData.host_by = null;
      if (!submitData.notes) submitData.notes = null;

      // Normalize links: drop entries without a URL; ensure label defaults to "Link"
      submitData.links = Array.isArray(submitData.links)
        ? submitData.links
            .filter((l) => l && typeof l.url === 'string' && l.url.trim() !== '')
            .map((l) => ({ url: l.url.trim(), label: (l.label || '').trim() || 'Link' }))
        : [];

      if (editingPost) {
        await axios.put(
          `${API}/social-media/posts/${editingPost.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post updated!');
      } else {
        await axios.post(
          `${API}/social-media/posts`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post created!');
      }
      
      setClashWarning(null);
      setIsDialogOpen(false);
      resetForm();
      loadPosts();
    } catch (error) {
      let errorMessage = 'Failed to save post';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
      console.error('Save error:', error.response?.data);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/social-media/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted!');
      loadPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const resetForm = () => {
    setFormData({
      content_name: '',
      platforms: [],
      account: '',
      college: '',
      content_type: '',
      language: '',
      host_by: '',
      approved_for_posting: false,
      content_received_date: '',
      scheduled_date: '',
      posted_date: '',
      status: 'Yet to Shoot',
      notes: '',
      links: [],
      assigned_to: ''
    });
    setEditingPost(null);
    setFormErrors({});
  };

  const handleDuplicatePost = async (postId) => {
    try {
      await axios.post(`${API}/social-media/posts/${postId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post duplicated successfully!');
      loadPosts();
    } catch (error) {
      toast.error('Failed to duplicate post');
      console.error('Duplicate error:', error);
    }
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      content_name: post.content_name,
      platforms: post.platforms || [],
      account: post.account || '',
      college: post.college || '',
      content_type: post.content_type || '',
      language: post.language || '',
      host_by: post.host_by || '',
      approved_for_posting: post.approved_for_posting || false,
      content_received_date: post.content_received_date ? format(new Date(post.content_received_date), 'yyyy-MM-dd') : '',
      scheduled_date: post.scheduled_date ? format(new Date(post.scheduled_date), 'yyyy-MM-dd') : '',
      posted_date: post.posted_date ? format(new Date(post.posted_date), 'yyyy-MM-dd') : '',
      status: post.status,
      notes: post.notes || '',
      links: Array.isArray(post.links) ? post.links.map(l => ({ url: l?.url || '', label: l?.label || '' })) : [],
      assigned_to: post.assigned_to || ''
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handlePlatformToggle = (platform) => {
    const newPlatforms = formData.platforms.includes(platform)
      ? formData.platforms.filter(p => p !== platform)
      : [...formData.platforms, platform];
    setFormData({ ...formData, platforms: newPlatforms });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({
      platform: [],
      account: [],
      college: [],
      content_type: [],
      language: [],
      status: [],
      created_by: [],
      assigned_to: []
    });
    setFromDate('');
    setToDate('');
    toast.success('All filters cleared');
  };

  const hasActiveFilters = () => {
    if (searchTerm || fromDate || toDate) return true;
    return Object.values(filters).some((v) => Array.isArray(v) && v.length > 0);
  };

  const clearDateFilters = () => {
    const currentMonth = getCurrentMonthDates();
    setFromDate(currentMonth.from);
    setToDate(currentMonth.to);
    setDateField('created_at'); // Reset to default Created Date
    toast.success('Date filters reset to current month');
  };

  // Handle row click to open post details
  const handleRowClick = (post, event) => {
    // Don't open modal if clicking on checkbox, action buttons, or their containers
    const target = event.target;
    const isActionElement = target.closest('[data-action-cell]') || 
                            target.closest('[data-checkbox-cell]') ||
                            target.closest('button') ||
                            target.closest('[role="checkbox"]');
    
    if (isActionElement) return;
    
    setSelectedPostDetail(post);
    setIsDetailModalOpen(true);
  };

  // Open edit dialog from detail modal
  const handleEditFromDetail = () => {
    setIsDetailModalOpen(false);
    openEditDialog(selectedPostDetail);
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPosts.length === paginatedPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(paginatedPosts.map(p => p.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedPosts.length} post(s)?`)) return;
    
    try {
      await Promise.all(
        selectedPosts.map(postId =>
          axios.delete(`${API}/social-media/posts/${postId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedPosts.length} post(s) deleted successfully!`);
      setSelectedPosts([]);
      loadPosts();
    } catch (error) {
      toast.error('Failed to delete posts');
    }
  };

  const openBulkUpdateDialog = () => {
    setBulkUpdateData({
      platforms: [],
      account: '',
      college: '',
      content_type: '',
      language: '',
      status: '',
      assigned_to: ''
    });
    setIsBulkUpdateDialogOpen(true);
  };

  const handleBulkUpdate = async () => {
    // Build update object with only non-empty fields (exclude "none" values)
    const updateFields = {};
    if (bulkUpdateData.platforms.length > 0) updateFields.platforms = bulkUpdateData.platforms;
    if (bulkUpdateData.account && bulkUpdateData.account !== 'none') updateFields.account = bulkUpdateData.account;
    if (bulkUpdateData.college && bulkUpdateData.college !== 'none') updateFields.college = bulkUpdateData.college;
    if (bulkUpdateData.content_type && bulkUpdateData.content_type !== 'none') updateFields.content_type = bulkUpdateData.content_type;
    if (bulkUpdateData.language && bulkUpdateData.language !== 'none') updateFields.language = bulkUpdateData.language;
    if (bulkUpdateData.status && bulkUpdateData.status !== 'none') updateFields.status = bulkUpdateData.status;
    // Handle assigned_to field - allow setting to empty string to unassign
    if (bulkUpdateData.assigned_to !== undefined && bulkUpdateData.assigned_to !== 'none') {
      updateFields.assigned_to = bulkUpdateData.assigned_to === 'unassigned' ? '' : bulkUpdateData.assigned_to;
    }

    if (Object.keys(updateFields).length === 0) {
      toast.error('Please select at least one field to update');
      return;
    }

    try {
      await axios.put(
        `${API}/social-media/posts/bulk-update`,
        {
          post_ids: selectedPosts,
          update_fields: updateFields
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully updated ${selectedPosts.length} post(s)!`);
      setIsBulkUpdateDialogOpen(false);
      setSelectedPosts([]);
      loadPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update posts');
    }
  };

  const handleBulkPlatformToggle = (platform) => {
    setBulkUpdateData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const exportCSV = () => {
    const headers = ['Content Name', 'Platforms', 'Account', 'College', 'Type', 'Status', 'Host By', 'Scheduled', 'Posted', 'Created By', 'Assigned To'];
    const rows = sortedPosts.map(p => [
      p.content_name,
      (p.platforms || []).join(', '),
      p.account || '',
      p.college || '',
      p.content_type || '',
      p.status,
      p.host_by || '',
      p.scheduled_date ? format(new Date(p.scheduled_date), 'yyyy-MM-dd') : '',
      p.posted_date ? format(new Date(p.posted_date), 'yyyy-MM-dd') : '',
      getUserName(p.created_by),
      p.assigned_to ? getUserName(p.assigned_to) : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${csvSafe(cell)}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `social-media-posts-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('CSV exported!');
  };

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = post.content_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.host_by || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!Array.isArray(value) || value.length === 0) return true;

        // Multi-select user filters (arrays of user IDs)
        if (key === 'created_by' || key === 'assigned_to') {
          return value.includes(post[key]);
        }

        // Platform filter — post.platforms is itself an array; OR across selected platforms.
        if (key === 'platform') {
          return Array.isArray(post.platforms) && value.some((v) => post.platforms.includes(v));
        }

        // Category-style filters (account/college/content_type/language/status) — OR across selections.
        return value.includes(post[key]);
      });

      // Date range filter based on selected date field
      let matchesDateRange = true;
      if (fromDate || toDate) {
        // Get the date field based on selection
        let postDate = null;
        if (dateField === 'scheduled_date') {
          postDate = post.scheduled_date ? new Date(post.scheduled_date) : null;
        } else if (dateField === 'created_at') {
          postDate = post.created_at ? new Date(post.created_at) : null;
        } else if (dateField === 'updated_at') {
          postDate = post.updated_at ? new Date(post.updated_at) : null;
        } else if (dateField === 'content_received_date') {
          postDate = post.content_received_date ? new Date(post.content_received_date) : null;
        } else if (dateField === 'posted_date') {
          postDate = post.posted_date ? new Date(post.posted_date) : null;
        }
        
        if (!postDate) {
          matchesDateRange = false;
        } else {
          const from = fromDate ? new Date(fromDate) : null;
          const to = toDate ? new Date(toDate) : null;
          
          // Set to end of day for 'to' date comparison
          if (to) {
            to.setHours(23, 59, 59, 999);
          }
          
          if (from && to) {
            matchesDateRange = postDate >= from && postDate <= to;
          } else if (from) {
            matchesDateRange = postDate >= from;
          } else if (to) {
            matchesDateRange = postDate <= to;
          }
        }
      }
      
      return matchesSearch && matchesFilters && matchesDateRange;
    });
  }, [posts, searchTerm, filters, fromDate, toDate, dateField]);

  // Sorted posts
  const sortedPosts = useMemo(() => {
    const sorted = [...filteredPosts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
      if (bVal == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle date fields
      if (['created_at', 'updated_at', 'scheduled_date', 'posted_date', 'content_received_date'].includes(sortField)) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle string comparisons
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      // Handle arrays (platforms)
      if (Array.isArray(aVal)) aVal = aVal.join(', ');
      if (Array.isArray(bVal)) bVal = bVal.join(', ');
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPosts, sortField, sortDirection]);

  // Paginated posts
  const totalPages = Math.ceil(sortedPosts.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedPosts = sortedPosts.slice(startIndex, endIndex);

  // --- Post Clash Detection (soft warning, never blocks save) ---
  // A "clash" = same Account + College + Scheduled Date across 2+ posts.
  // Computed from the full `posts` list (not filtered) so the badge always
  // reflects true duplicates regardless of active table filters.
  // Extract the 'YYYY-MM-DD' portion directly via string slicing rather than
  // round-tripping through `new Date()` — avoids timezone bugs where a bare
  // form value ('2025-12-01') and a backend ISO datetime
  // ('2025-12-01T00:00:00+00:00') could parse to different local dates.
  // Parse a 'YYYY-MM-DD' (or longer ISO) string as a LOCAL calendar date
  // (no UTC-vs-local day-shift), safe for display formatting.
  const parseDateOnly = (val) => {
    if (!val) return null;
    const [y, m, d] = val.slice(0, 10).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

  const toDateKey = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val.slice(0, 10);
    return format(val, 'yyyy-MM-dd');
  };

  const clashKeyOf = (accountVal, collegeVal, scheduledDateVal) => {
    if (!accountVal || !collegeVal || !scheduledDateVal) return null;
    const dateKey = toDateKey(scheduledDateVal);
    return `${accountVal}|||${collegeVal}|||${dateKey}`;
  };

  const clashGroups = useMemo(() => {
    const groups = {};
    posts.forEach((p) => {
      const key = clashKeyOf(p.account, p.college, p.scheduled_date);
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }, [posts]);

  const clashedPostIds = useMemo(() => {
    const ids = new Set();
    Object.values(clashGroups).forEach((group) => {
      if (group.length > 1) group.forEach((p) => ids.add(p.id));
    });
    return ids;
  }, [clashGroups]);

  // Other posts that clash with a given post (for badge tooltip / warning list)
  const getClashPartners = (post) => {
    const key = clashKeyOf(post.account, post.college, post.scheduled_date);
    if (!key) return [];
    return (clashGroups[key] || []).filter((p) => p.id !== post.id);
  };

  // Pre-save clash check — excludes the post being edited from matching itself.
  const findClashingPosts = (accountVal, collegeVal, scheduledDateVal, excludeId) => {
    if (!accountVal || !collegeVal || !scheduledDateVal) return [];
    const targetKey = toDateKey(scheduledDateVal);
    return posts.filter((p) => {
      if (excludeId && p.id === excludeId) return false;
      if (p.account !== accountVal || p.college !== collegeVal) return false;
      if (!p.scheduled_date) return false;
      return toDateKey(p.scheduled_date) === targetKey;
    });
  };

  const [clashWarning, setClashWarning] = useState(null); // { matches: Post[] } | null

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, fromDate, toDate, dateField, sortField, sortDirection, rowsPerPage]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Yet to Shoot': 'bg-slate-100 text-slate-700',
      'In Shoot': 'bg-blue-100 text-blue-700',
      'Yet to Edit': 'bg-indigo-100 text-indigo-700',
      'In Editing': 'bg-purple-100 text-purple-700',
      'In Corrections': 'bg-orange-100 text-orange-700',
      'Not Approved': 'bg-red-100 text-red-700',
      'Yet to Deliver': 'bg-pink-100 text-pink-700',
      'Out Delivered': 'bg-teal-100 text-teal-700',
      'Waiting for Approval': 'bg-amber-100 text-amber-700',
      'On-Hold': 'bg-gray-100 text-gray-700',
      'Scheduled': 'bg-yellow-100 text-yellow-700',
      'Published': 'bg-lime-100 text-lime-700',
      // Legacy status support
      'Pending': 'bg-slate-100 text-slate-700',
      'Completed': 'bg-lime-100 text-lime-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const downloadSampleTemplate = async () => {
    try {
      const response = await axios.get(`${API}/social-media/sample-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Social_Media_Sample_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sample template downloaded!');
    } catch (error) {
      toast.error('Failed to download sample template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Uploading and processing file...');
      const response = await axios.post(`${API}/social-media/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.dismiss();

      const data = response.data;
      setUploadResult(data);

      if (data.imported_count > 0) {
        toast.success(`Imported ${data.imported_count} of ${data.total_rows} rows`);
      }
      // Open the detailed results modal whenever there is anything to review.
      if ((data.error_count || 0) > 0 || (data.warning_count || 0) > 0) {
        setIsUploadResultOpen(true);
        if (data.imported_count === 0) {
          toast.error(`Imported 0 posts. ${data.error_count} row(s) had errors — see details.`, { duration: 5000 });
        }
      }

      loadPosts();
      e.target.value = ''; // Reset file input
    } catch (error) {
      toast.dismiss();
      const errorMsg = error.response?.data?.detail || 'Failed to upload file';
      toast.error(errorMsg);
      e.target.value = '';
    }
  };

  // Build and download a CSV report of failed rows + warnings from the last import.
  const downloadErrorReport = () => {
    if (!uploadResult) return;
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Row', 'Content Name', 'Type', 'Issue'].map(escape).join(',')];
    (uploadResult.errors || []).forEach((er) =>
      lines.push([er.row, er.content_name, 'Error', er.message].map(escape).join(','))
    );
    (uploadResult.warnings || []).forEach((w) =>
      lines.push([w.row, w.content_name, 'Warning', w.message].map(escape).join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Social_Media_Import_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-posts-table">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Social Media Posts</h1>
          <p className="page-subtitle">Manage content posting schedule</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={downloadSampleTemplate}
            variant="outline"
            data-testid="download-sample"
            title="Sample columns (in form order): Content Name, Platforms, Account, College, Content Type, Language, Host By, Content Received Date, Scheduled Date, Posted Date, Status, Approved for Posting, Assigned To (email / name / user id), Notes"
          >
            <FileSpreadsheet size={18} className="mr-2" /> Download Sample
          </Button>

          <Button
            variant="outline"
            onClick={() => document.getElementById('bulk-upload-input').click()}
            data-testid="bulk-upload-button"
            title="Upload an .xlsx / .xls / .csv with columns matching the sample template. 'Language' falls back to 'Others' for unknown values; 'Assigned To' accepts user email, full name, or user id."
          >
            <Upload size={18} className="mr-2" /> Bulk Upload
          </Button>
          <input
            id="bulk-upload-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleBulkUpload}
            style={{ display: 'none' }}
          />

          {/* Bulk Upload Results — row-wise errors & warnings */}
          <Dialog open={isUploadResultOpen} onOpenChange={setIsUploadResultOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-upload-result-modal">
              <DialogHeader>
                <DialogTitle>Bulk Import Results</DialogTitle>
              </DialogHeader>
              {uploadResult && (
                <div className="space-y-4">
                  {/* Summary cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="import-summary-imported">
                      <div className="flex items-center justify-center gap-1.5 text-emerald-700">
                        <CheckCircle2 size={16} />
                        <span className="text-2xl font-bold">{uploadResult.imported_count}</span>
                      </div>
                      <p className="text-xs text-emerald-700 mt-0.5">Imported</p>
                    </div>
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center" data-testid="import-summary-failed">
                      <div className="flex items-center justify-center gap-1.5 text-red-700">
                        <X size={16} />
                        <span className="text-2xl font-bold">{uploadResult.error_count || 0}</span>
                      </div>
                      <p className="text-xs text-red-700 mt-0.5">Failed rows</p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center" data-testid="import-summary-warnings">
                      <div className="flex items-center justify-center gap-1.5 text-amber-700">
                        <AlertTriangle size={16} />
                        <span className="text-2xl font-bold">{uploadResult.warning_count || 0}</span>
                      </div>
                      <p className="text-xs text-amber-700 mt-0.5">Warnings</p>
                    </div>
                  </div>

                  {((uploadResult.errors || []).length > 0 || (uploadResult.warnings || []).length > 0) && (
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="download-error-report">
                        <FileDown size={16} className="mr-2" /> Download Error Report (CSV)
                      </Button>
                    </div>
                  )}

                  {/* Errors table */}
                  {(uploadResult.errors || []).length > 0 && (
                    <div data-testid="import-errors-list">
                      <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                        <X size={14} /> Failed rows ({uploadResult.errors.length})
                      </h4>
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-3 py-2 text-left w-16">Row</th>
                              <th className="px-3 py-2 text-left w-48">Content Name</th>
                              <th className="px-3 py-2 text-left">Issue</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {uploadResult.errors.map((er, i) => (
                              <tr key={`err-${i}`} className="hover:bg-red-50/40">
                                <td className="px-3 py-2 font-medium text-slate-700">{er.row}</td>
                                <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]" title={er.content_name}>{er.content_name || '—'}</td>
                                <td className="px-3 py-2 text-red-700">{er.message}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Warnings table */}
                  {(uploadResult.warnings || []).length > 0 && (
                    <div data-testid="import-warnings-list">
                      <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                        <AlertTriangle size={14} /> Warnings ({uploadResult.warnings.length}) — these rows were imported
                      </h4>
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-3 py-2 text-left w-16">Row</th>
                              <th className="px-3 py-2 text-left w-48">Content Name</th>
                              <th className="px-3 py-2 text-left">Note</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {uploadResult.warnings.map((w, i) => (
                              <tr key={`warn-${i}`} className="hover:bg-amber-50/40">
                                <td className="px-3 py-2 font-medium text-slate-700">{w.row}</td>
                                <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]" title={w.content_name}>{w.content_name || '—'}</td>
                                <td className="px-3 py-2 text-amber-700">{w.message}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-1">
                    <Button onClick={() => setIsUploadResultOpen(false)} data-testid="close-upload-result">Close</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          <Button onClick={exportCSV} variant="outline" data-testid="export-csv">
            <Download size={18} className="mr-2" /> Export
          </Button>
          {canModify && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-post-button" className="btn-cta">
                  <Plus size={18} className="mr-2" /> Add Post
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="content-name-input">
                      Content Name / Thumbnail Name <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="content-name-input"
                      value={formData.content_name}
                      onChange={(e) => {
                        setFormData({...formData, content_name: e.target.value});
                        if (formErrors.content_name) setFormErrors(({ content_name, ...rest }) => rest);
                      }}
                      data-testid="content-name-input"
                      aria-invalid={!!formErrors.content_name}
                      className={formErrors.content_name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {formErrors.content_name && (
                      <p data-testid="content-name-error" className="text-xs text-red-600 font-medium">
                        {formErrors.content_name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Platforms <span className="text-red-500" aria-hidden="true">*</span>
                      <span className="text-slate-500 font-normal"> (Select one or more)</span>
                    </Label>
                    <div
                      className={`grid grid-cols-2 gap-3 ${formErrors.platforms ? 'p-2 rounded-md border border-red-500 bg-red-50/30' : ''}`}
                    >
                      {PLATFORMS.map(platform => (
                        <div key={platform} className="flex items-center gap-2">
                          <Checkbox
                            checked={formData.platforms.includes(platform)}
                            onCheckedChange={() => {
                              handlePlatformToggle(platform);
                              if (formErrors.platforms) setFormErrors(({ platforms, ...rest }) => rest);
                            }}
                          data-testid={`platform-${platform.toLowerCase()}`}
                        />
                        <Label>{platform}</Label>
                      </div>
                    ))}
                  </div>
                  {formErrors.platforms && (
                    <p data-testid="platforms-error" className="text-xs text-red-600 font-medium">
                      {formErrors.platforms}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Account <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.account || undefined}
                      onValueChange={(v) => {
                        setFormData({...formData, account: v});
                        if (formErrors.account) setFormErrors(({ account, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="account-select"
                        aria-invalid={!!formErrors.account}
                        className={formErrors.account ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCOUNTS.map(acc => (
                          <SelectItem key={acc} value={acc}>{acc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.account && (
                      <p data-testid="account-error" className="text-xs text-red-600 font-medium">
                        {formErrors.account}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      College <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.college || undefined}
                      onValueChange={(v) => {
                        setFormData({...formData, college: v});
                        if (formErrors.college) setFormErrors(({ college, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="college-select"
                        aria-invalid={!!formErrors.college}
                        className={formErrors.college ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select college" />
                      </SelectTrigger>
                      <SelectContent>
                        {COLLEGES.map(col => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                        {formData.college && !COLLEGES.includes(formData.college) && (
                          <SelectItem key={formData.college} value={formData.college} data-testid="college-archived-option">
                            {formData.college} (archived)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {formErrors.college && (
                      <p data-testid="college-error" className="text-xs text-red-600 font-medium">
                        {formErrors.college}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Content Type <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.content_type || undefined}
                      onValueChange={(v) => {
                        setFormData({...formData, content_type: v});
                        if (formErrors.content_type) setFormErrors(({ content_type, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="content-type-select"
                        aria-invalid={!!formErrors.content_type}
                        className={formErrors.content_type ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTENT_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.content_type && (
                      <p data-testid="content-type-error" className="text-xs text-red-600 font-medium">
                        {formErrors.content_type}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Language <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.language || undefined}
                      onValueChange={(v) => {
                        setFormData({...formData, language: v});
                        if (formErrors.language) setFormErrors(({ language, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="language-select"
                        aria-invalid={!!formErrors.language}
                        className={formErrors.language ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(lang => (
                          <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.language && (
                      <p data-testid="language-error" className="text-xs text-red-600 font-medium">
                        {formErrors.language}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="host-by-input">
                      Host By <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="host-by-input"
                      value={formData.host_by}
                      onChange={(e) => {
                        setFormData({...formData, host_by: e.target.value});
                        if (formErrors.host_by) setFormErrors(({ host_by, ...rest }) => rest);
                      }}
                      data-testid="host-by-input"
                      aria-invalid={!!formErrors.host_by}
                      className={formErrors.host_by ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {formErrors.host_by && (
                      <p data-testid="host-by-error" className="text-xs text-red-600 font-medium">
                        {formErrors.host_by}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Content Received</Label>
                    <DatePicker
                      value={formData.content_received_date}
                      onChange={(v) => setFormData({...formData, content_received_date: v})}
                      testId="content-received-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Scheduled Date</Label>
                    <DatePicker
                      value={formData.scheduled_date}
                      onChange={(v) => setFormData({...formData, scheduled_date: v})}
                      testId="scheduled-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Posted Date</Label>
                    <DatePicker
                      value={formData.posted_date}
                      onChange={(v) => setFormData({...formData, posted_date: v})}
                      testId="posted-date-input"
                    />
                  </div>
                </div>

                {/* Status / Approved for Posting / Assigned To.
                    Desktop: 30% / 30% / 40% via grid-cols-10 (col-spans 3/3/4).
                    Tablet:  Status + Approved on row 1, Assigned To full-width row 2.
                    Mobile:  All three stack vertically. */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-10 gap-4 items-start">
                  <div className="space-y-2 lg:col-span-3">
                    <Label className="block h-5 leading-5">
                      Status <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => {
                        setFormData({...formData, status: v});
                        if (formErrors.status) setFormErrors(({ status, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="status-select"
                        aria-invalid={!!formErrors.status}
                        className={`h-10 ${formErrors.status ? 'border-red-500 focus:ring-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.status && (
                      <p data-testid="status-error" className="text-xs text-red-600 font-medium">
                        {formErrors.status}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 lg:col-span-3">
                    <Label htmlFor="approved-for-posting-toggle" className="block h-5 leading-5">
                      Approved for Posting
                    </Label>
                    <div className="h-10 flex items-center gap-3 px-3 rounded-md border border-input bg-background">
                      <Switch
                        id="approved-for-posting-toggle"
                        checked={!!formData.approved_for_posting}
                        onCheckedChange={(checked) => setFormData({...formData, approved_for_posting: checked})}
                        data-testid="approved-for-posting-switch"
                        aria-label="Toggle Approved for Posting"
                      />
                      <span
                        className={`text-sm font-medium ${formData.approved_for_posting ? 'text-emerald-600' : 'text-slate-500'}`}
                        data-testid="approved-for-posting-state"
                      >
                        {formData.approved_for_posting ? 'Approved' : 'Not approved'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 lg:col-span-4">
                    <Label className="flex items-center gap-1.5 h-5 leading-5">
                      <User size={14} aria-hidden="true" />
                      <span>Assigned To</span>
                      <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.assigned_to || undefined}
                      onValueChange={(v) => {
                        setFormData({...formData, assigned_to: v});
                        if (formErrors.assigned_to) setFormErrors(({ assigned_to, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="assigned-to-select"
                        aria-invalid={!!formErrors.assigned_to}
                        className={`h-10 ${formErrors.assigned_to ? 'border-red-500 focus:ring-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Select assignee">
                          {formData.assigned_to ? (
                            <UserAvatar user={users.find(u => u.id === formData.assigned_to)} size="sm" showName />
                          ) : (
                            <span className="text-slate-400">Select assignee</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            <UserAvatar user={u} size="sm" showName />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.assigned_to && (
                      <p data-testid="assigned-to-error" className="text-xs text-red-600 font-medium">
                        {formErrors.assigned_to}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes / Comments</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                  />
                </div>

                {/* Links / URLs */}
                <div className="space-y-2" data-testid="post-links-section">
                  <MultiLinkInput
                    links={formData.links}
                    onChange={(links) => setFormData({...formData, links})}
                  />
                </div>

                {/* Created/Updated Information - Only show when editing */}
                {editingPost && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-slate-700">
                          Created: {editingPost.created_at 
                            ? format(new Date(editingPost.created_at), 'MMM dd, yyyy h:mm a')
                            : 'N/A'}
                        </p>
                        <p className="text-slate-600">
                          by {getUserName(editingPost.created_by)}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">
                          Last Updated: {editingPost.updated_at 
                            ? format(new Date(editingPost.updated_at), 'MMM dd, yyyy h:mm a')
                            : 'N/A'}
                        </p>
                        <p className="text-slate-600">
                          by {getUserName(editingPost.updated_by || editingPost.created_by)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-post-button">{editingPost ? 'Update' : 'Create'} Post</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}

          {/* Post Clash Warning — soft warning only, never blocks save */}
          <AlertDialog open={!!clashWarning} onOpenChange={(open) => { if (!open) setClashWarning(null); }}>
            <AlertDialogContent data-testid="post-clash-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle size={20} />
                  Potential Post Clash
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-slate-600 space-y-3">
                    <p>
                      {clashWarning?.matches?.length === 1
                        ? 'A post already exists for '
                        : `${clashWarning?.matches?.length || 0} posts already exist for `}
                      <span className="font-semibold">{formData.account} → {formData.college}</span>
                      {' '}on{' '}
                      <span className="font-semibold">
                        {formData.scheduled_date ? format(parseDateOnly(formData.scheduled_date), 'MMM dd, yyyy') : ''}
                      </span>.
                      Please review the existing post{clashWarning?.matches?.length === 1 ? '' : 's'} to avoid duplicate or overlapping content.
                    </p>
                    <div className="rounded-md border border-amber-200 bg-amber-50 divide-y divide-amber-100" data-testid="post-clash-list">
                      {(clashWarning?.matches || []).map((m) => (
                        <div key={m.id} className="px-3 py-2 text-sm" data-testid={`post-clash-item-${m.id}`}>
                          <p className="font-medium text-slate-800">{m.content_name}</p>
                          <p className="text-xs text-slate-500">
                            {m.status || 'No status'} {m.platforms?.length ? `• ${m.platforms.join(', ')}` : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">This is a warning only — you can still save this post.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="post-clash-cancel-button">Cancel / Review Existing Post</AlertDialogCancel>
                <AlertDialogAction
                  onClick={savePost}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="post-clash-continue-button"
                >
                  Continue & {editingPost ? 'Update' : 'Create'} Post
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Bulk Update Dialog */}
          {canModify && (
          <Dialog open={isBulkUpdateDialogOpen} onOpenChange={setIsBulkUpdateDialogOpen}>
            <DialogContent className="max-w-2xl" data-testid="bulk-update-dialog">
              <DialogHeader>
                <DialogTitle>Bulk Update {selectedPosts.length} Post(s)</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-600">
                  Select the fields you want to update. Only filled fields will be updated for all selected posts.
                </p>

                {/* Platforms */}
                <div>
                  <Label className="mb-2 block">Platforms (optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map(platform => (
                      <Button
                        key={platform}
                        type="button"
                        size="sm"
                        variant={bulkUpdateData.platforms.includes(platform) ? "default" : "outline"}
                        onClick={() => handleBulkPlatformToggle(platform)}
                        data-testid={`bulk-platform-${platform.toLowerCase()}`}
                      >
                        {platform}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Account */}
                <div>
                  <Label htmlFor="bulk-account">Account (optional)</Label>
                  <Select value={bulkUpdateData.account} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, account: value})}>
                    <SelectTrigger id="bulk-account" data-testid="bulk-account-select">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don&apos;t change</SelectItem>
                      {ACCOUNTS.map(account => (
                        <SelectItem key={account} value={account}>{account}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* College */}
                <div>
                  <Label htmlFor="bulk-college">College (optional)</Label>
                  <Select value={bulkUpdateData.college} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, college: value})}>
                    <SelectTrigger id="bulk-college" data-testid="bulk-college-select">
                      <SelectValue placeholder="Select college" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don&apos;t change</SelectItem>
                      {COLLEGES.map(college => (
                        <SelectItem key={college} value={college}>{college}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Content Type */}
                <div>
                  <Label htmlFor="bulk-content-type">Content Type (optional)</Label>
                  <Select value={bulkUpdateData.content_type} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, content_type: value})}>
                    <SelectTrigger id="bulk-content-type" data-testid="bulk-content-type-select">
                      <SelectValue placeholder="Select content type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don&apos;t change</SelectItem>
                      {CONTENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Language */}
                <div>
                  <Label htmlFor="bulk-language">Language (optional)</Label>
                  <Select value={bulkUpdateData.language} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, language: value})}>
                    <SelectTrigger id="bulk-language" data-testid="bulk-language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don&apos;t change</SelectItem>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status */}
                <div>
                  <Label htmlFor="bulk-status">Status (optional)</Label>
                  <Select value={bulkUpdateData.status} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, status: value})}>
                    <SelectTrigger id="bulk-status" data-testid="bulk-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don&apos;t change</SelectItem>
                      {STATUSES.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assigned To - gated by social_media.edit (Phase 4) */}
                {can('social_media.edit') && (
                  <div>
                    <Label htmlFor="bulk-assigned-to">Assigned To (optional)</Label>
                    <Select value={bulkUpdateData.assigned_to} onValueChange={(value) => setBulkUpdateData({...bulkUpdateData, assigned_to: value})}>
                      <SelectTrigger id="bulk-assigned-to" data-testid="bulk-assigned-to-select">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Don&apos;t change</SelectItem>
                        <SelectItem value="unassigned">Unassign</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-medium">
                                {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              {u.full_name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsBulkUpdateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleBulkUpdate} data-testid="bulk-update-submit">
                    Update {selectedPosts.length} Post(s)
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-start gap-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-9 gap-3 flex-1">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-3 text-slate-400" size={18} />
              <Input
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-posts-input"
              />
            </div>

            {/* Created By multi-select */}
            <MultiSelectFilter
              label="Created By"
              placeholder="Created By"
              options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
              selectedValues={filters.created_by}
              onChange={(ids) => setFilters({ ...filters, created_by: ids })}
              testId="filter-created-by"
            />

            {/* Assigned To multi-select */}
            <MultiSelectFilter
              label="Assigned To"
              placeholder="Assigned To"
              options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
              selectedValues={filters.assigned_to}
              onChange={(ids) => setFilters({ ...filters, assigned_to: ids })}
              testId="filter-assigned-to"
            />

            {['platform', 'account', 'college', 'content_type', 'language', 'status'].map(filterKey => {
              const OPTIONS_MAP = { platform: PLATFORMS, account: ACCOUNTS, college: COLLEGES, content_type: CONTENT_TYPES, language: LANGUAGES, status: STATUSES };
              const niceLabel = filterKey.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
              return (
                <MultiSelectFilter
                  key={filterKey}
                  label={niceLabel}
                  placeholder={`All ${niceLabel}`}
                  options={OPTIONS_MAP[filterKey]}
                  selectedValues={filters[filterKey]}
                  onChange={(vals) => setFilters({ ...filters, [filterKey]: vals })}
                  testId={`filter-${filterKey}`}
                />
              );
            })}
          </div>
          {hasActiveFilters() && (
            <Button
              variant="outline"
              onClick={clearAllFilters}
              className="flex items-center gap-2 whitespace-nowrap"
              data-testid="clear-filters-button"
            >
              <X size={16} />
              Clear All
            </Button>
          )}
        </div>

        <ActiveFilterChips
          chips={[
            filters.created_by.length > 0 && { key: 'created-by', label: `${filters.created_by.length} Creator${filters.created_by.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, created_by: [] }) },
            filters.assigned_to.length > 0 && { key: 'assigned-to', label: `${filters.assigned_to.length} Assignee${filters.assigned_to.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, assigned_to: [] }) },
            filters.platform.length > 0 && { key: 'platform', label: `${filters.platform.length} Platform${filters.platform.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, platform: [] }) },
            filters.account.length > 0 && { key: 'account', label: `${filters.account.length} Account${filters.account.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, account: [] }) },
            filters.college.length > 0 && { key: 'college', label: `${filters.college.length} College${filters.college.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, college: [] }) },
            filters.content_type.length > 0 && { key: 'content-type', label: `${filters.content_type.length} Content Type${filters.content_type.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, content_type: [] }) },
            filters.language.length > 0 && { key: 'language', label: `${filters.language.length} Language${filters.language.length > 1 ? 's' : ''}`, onRemove: () => setFilters({ ...filters, language: [] }) },
            filters.status.length > 0 && { key: 'status', label: `${filters.status.length} Status${filters.status.length > 1 ? 'es' : ''}`, onRemove: () => setFilters({ ...filters, status: [] }) },
          ].filter(Boolean)}
          onClearAll={hasActiveFilters() ? clearAllFilters : null}
        />

        {/* Date Range Filter Row */}
        <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <Calendar className="text-cyan-500" size={18} />
            <Label className="text-sm font-medium text-slate-700">Date Range:</Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">Date Field:</Label>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger className="w-[160px]" data-testid="date-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled_date">Scheduled Date</SelectItem>
                <SelectItem value="created_at">Created</SelectItem>
                <SelectItem value="updated_at">Last Updated</SelectItem>
                <SelectItem value="content_received_date">Content Received</SelectItem>
                <SelectItem value="posted_date">Posted Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">From:</Label>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              className="w-40"
              testId="filter-from-date"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">To:</Label>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              className="w-40"
              testId="filter-to-date"
            />
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={clearDateFilters}
            className="flex items-center gap-1"
            data-testid="reset-date-filters"
          >
            <X size={14} />
            Reset to Current Month
          </Button>
          
          <div className="ml-auto text-sm font-medium text-slate-600">
            Showing <span className="text-cyan-600 font-bold">{filteredPosts.length}</span> of <span className="font-bold">{posts.length}</span> posts
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedPosts.length > 0 && canModify && (
        <div className="btn-cta rounded-xl shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <span className="font-semibold">{selectedPosts.length} post(s) selected</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPosts([])}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
              >
                <X size={14} className="mr-1" />
                Clear Selection
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openBulkUpdateDialog}
                className="bg-white text-cyan-600 border-white hover:bg-white/90"
                data-testid="bulk-update-button"
              >
                <Edit size={16} className="mr-2" />
                Update Selected
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-red-500 text-white border-red-600 hover:bg-red-600"
                  data-testid="bulk-delete-button"
                >
                  <Trash size={16} className="mr-2" />
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RBAC Feedback Banner */}
      {user && !can('social_media.approve') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="rbac-info-banner">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-sm text-blue-700">
            You are viewing content based on your assigned brands and tasks. Contact your manager for additional access.
          </p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {canModify && (
                  <th className="px-4 py-4 text-left">
                    <Checkbox
                      checked={selectedPosts.length === paginatedPosts.length && paginatedPosts.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('content_name')}
                >
                  <div className="flex items-center gap-1">
                    Content
                    <SortIndicator field="content_name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('platforms')}
                >
                  <div className="flex items-center gap-1">
                    Platforms
                    <SortIndicator field="platforms" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('account')}
                >
                  <div className="flex items-center gap-1">
                    Account
                    <SortIndicator field="account" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('college')}
                >
                  <div className="flex items-center gap-1">
                    College
                    <SortIndicator field="college" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('content_type')}
                >
                  <div className="flex items-center gap-1">
                    Type
                    <SortIndicator field="content_type" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIndicator field="status" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('content_received_date')}
                >
                  <div className="flex items-center gap-1">
                    Content Received
                    <SortIndicator field="content_received_date" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('scheduled_date')}
                >
                  <div className="flex items-center gap-1">
                    Scheduled
                    <SortIndicator field="scheduled_date" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('posted_date')}
                >
                  <div className="flex items-center gap-1">
                    Posted Date
                    <SortIndicator field="posted_date" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('created_by')}
                >
                  <div className="flex items-center gap-1">
                    Created By
                    <SortIndicator field="created_by" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('assigned_to')}
                >
                  <div className="flex items-center gap-1">
                    Assigned To
                    <SortIndicator field="assigned_to" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                {canModify && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedPosts.map((post) => (
                <tr 
                  key={post.id} 
                  className={`cursor-pointer transition-colors ${clashedPostIds.has(post.id) ? 'bg-amber-50/70 hover:bg-amber-100/80' : 'hover:bg-cyan-50'}`}
                  onClick={(e) => handleRowClick(post, e)}
                  data-testid={`post-row-${post.id}`}
                >
                  {canModify && (
                    <td className="px-4 py-4" data-checkbox-cell>
                      <Checkbox
                        checked={selectedPosts.includes(post.id)}
                        onCheckedChange={() => togglePostSelection(post.id)}
                        data-testid={`post-checkbox-${post.id}`}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{post.content_name}</p>
                        {clashedPostIds.has(post.id) && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300"
                            title={`Same Account + College + Scheduled Date as: ${getClashPartners(post).map(p => p.content_name).join(', ')}`}
                            data-testid={`post-clash-badge-${post.id}`}
                          >
                            <AlertTriangle size={10} />
                            Potential Clash
                          </span>
                        )}
                      </div>
                      {post.host_by && <p className="text-sm text-slate-500">Host: {post.host_by}</p>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5" data-testid={`post-platforms-${post.id}`}>
                      {(post.platforms || []).map(p => {
                        const linkUrl = findPlatformLink(p, post.links, PLATFORM_ICONS);
                        return (
                          <button
                            key={p}
                            type="button"
                            title={linkUrl ? p : `No ${p} link available`}
                            aria-label={linkUrl ? `Open ${p} link` : `No ${p} link available`}
                            data-testid={`platform-icon-${post.id}-${p.replace(/\s+/g, '-')}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (linkUrl) window.open(linkUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                              linkUrl ? 'hover:bg-slate-100 cursor-pointer' : 'opacity-40 cursor-default'
                            }`}
                          >
                            <PlatformIcon platform={p} size={16} iconMap={PLATFORM_ICONS} />
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {post.account ? (() => {
                      const badge = resolveBadgeStyle(post.account, ACCOUNT_COLORS);
                      return (
                        <span
                          className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`}
                          style={badge.style}
                          data-testid={`post-account-${post.id}`}
                        >
                          {post.account}
                        </span>
                      );
                    })() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const collegeLabel = post.college || 'No College';
                      const badge = resolveBadgeStyle(collegeLabel, COLLEGE_COLORS);
                      return (
                        <span
                          className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`}
                          style={badge.style}
                          data-testid={`post-college-${post.id}`}
                        >
                          {collegeLabel}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    {post.content_type ? (() => {
                      const badge = resolveBadgeStyle(post.content_type, CONTENT_TYPE_COLORS);
                      return (
                        <span
                          className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`}
                          style={badge.style}
                          data-testid={`post-content-type-${post.id}`}
                        >
                          {post.content_type}
                        </span>
                      );
                    })() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {post.content_received_date ? format(new Date(post.content_received_date), 'MMM dd, yyyy') : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {post.scheduled_date ? format(new Date(post.scheduled_date), 'MMM dd, yyyy') : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {post.posted_date ? format(new Date(post.posted_date), 'MMM dd, yyyy') : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {getUserName(post.created_by)}
                  </td>
                  <td className="px-6 py-4">
                    {post.assigned_to ? (
                      <UserAvatar user={users.find(u => u.id === post.assigned_to)} size="sm" showName />
                    ) : (
                      <span className="text-slate-400 text-sm italic">Unassigned</span>
                    )}
                  </td>
                  {canModify && (
                    <td className="px-6 py-4" data-action-cell>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDuplicatePost(post.id)}
                          title="Duplicate Post"
                          data-testid={`duplicate-post-${post.id}`}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Copy size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(post)} title="Edit Post" data-testid={`edit-post-${post.id}`}>
                          <Edit size={16} />
                        </Button>
                        {canDelete && (
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(post.id)} className="text-red-600 hover:bg-red-50" title="Delete Post" data-testid={`delete-post-${post.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {paginatedPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">No posts found</p>
            </div>
          )}
        </div>
        
        {/* Pagination Controls */}
        {sortedPosts.length > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between">
              {/* Rows per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(Number(v))}>
                  <SelectTrigger className="w-[70px] h-8" data-testid="rows-per-page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">rows</span>
              </div>

              {/* Showing X to Y of Z entries */}
              <div className="text-sm text-slate-600">
                Showing <span className="font-semibold text-slate-900">{startIndex + 1}</span> to{' '}
                <span className="font-semibold text-slate-900">{Math.min(endIndex, sortedPosts.length)}</span> of{' '}
                <span className="font-semibold text-slate-900">{sortedPosts.length}</span> entries
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8"
                  data-testid="prev-page"
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm text-slate-600">Page</span>
                  <span className="text-sm font-semibold text-slate-900">{currentPage}</span>
                  <span className="text-sm text-slate-600">of</span>
                  <span className="text-sm font-semibold text-slate-900">{totalPages || 1}</span>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="h-8"
                  data-testid="next-page"
                >
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Post Detail Modal - Using standardized component */}
      <PostDetailModal
        isOpen={isDetailModalOpen}
        onClose={setIsDetailModalOpen}
        post={selectedPostDetail}
        users={users}
        canModify={canModify}
        onEdit={handleEditFromDetail}
        getUserName={getUserName}
      />
    </div>
  );
};

export default PostsTable;
